import i18n from "i18next";
import { type FC, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { useAidPreferenceDeleteMutation } from "src/app/features/EmvKernelProfile/subFeatures/AidPreference/services/server/AidPreferenceApi";
import type { AidPreferenceEntity } from "src/app/features/EmvKernelProfile/subFeatures/AidPreference/types/AidPreferenceEntity";
import type { ExtendedColumnsType } from "../../../../../../../components";
import type { AntCustomFormField, CommonFieldAttributes } from "../../../../../../../components/Form/types/types";
import { PropertyTable } from "../../../../../../../components/Table/components/PropertyTable";
import { Actions } from "../../../../../../../components/Table/types/types";
import { ResourceName, ResourceVariant } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import {
    showSuccessNotification,
    showWarningNotification,
} from "../../../../../../../core/setup/redux/state/notifications/notificationsState";

const getColumnsConfig = (t: Function): ExtendedColumnsType<AidPreferenceEntity>[] => {
    return [
        {
            title: t("emvKernelConfiguration.id"),
            key: "id",
            dataIndex: "id",
        },
        {
            title: t("emvKernelConfigurationCreateForm.aidPrimary"),
            key: "aidPrimary",
            dataIndex: "aidPrimary",
        },
        {
            title: t("emvKernelConfigurationCreateForm.appSelectionIndicatorAidShort"),
            key: "appSelectionIndicatorAid",
            dataIndex: "appSelectionIndicatorAid",
        },
        {
            title: t("emvKernelConfigurationCreateForm.aidsSecondary"),
            key: "aidsSecondary",
            dataIndex: "aidsSecondary",
        },
        {
            title: t("emvKernelConfigurationCreateForm.sequence"),
            key: "sequence",
            dataIndex: "sequence",
        },
    ];
};

export interface AidPreferenceTableProps extends AntCustomFormField<AidPreferenceEntity[]>, CommonFieldAttributes {}

export const AidPreferencesTable: FC<AidPreferenceTableProps> = ({ value = [], onChange, readOnly }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const resourceUrl = `${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${id}/${ResourceName.AID_PREFERENCES}`;

    const onDataChanged = (data: AidPreferenceEntity[]) => {
        if (onChange) {
            onChange(data);
        }
    };

    const onRowClick = (record: AidPreferenceEntity) => navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.EDIT}/${record.id}`);

    const addCallback = () => {
        navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.CREATE}`);
    };

    const [deleteTableItem] = useAidPreferenceDeleteMutation();

    const deleteCallback = (item: AidPreferenceEntity) => {
        deleteTableItem({
            emvKernelProfileId: id,
            id: item.id,
        })
            .unwrap()
            .then((payload) => performDeleteSuccess(payload))
            .catch(() => {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("common.formDeleteError", {
                            id: item.id,
                        }),
                    }),
                );
            });

        const performDeleteSuccess = (payload: AidPreferenceEntity) => {
            if (payload) {
                notifyOkDelete(payload);
            }
            onDataChanged(value.filter((record) => record.id !== item.id));
        };
    };

    const notifyOkDelete = useCallback(
        (payload: AidPreferenceEntity) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formDeleteSuccess", {
                        id: payload.id,
                        resource: payload.name,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    return (
        <PropertyTable<AidPreferenceEntity>
            getColumnsConfig={getColumnsConfig}
            name="aidPreferences_table"
            propertyAsRowKey="id"
            tableData={value || []}
            readOnly={readOnly || false}
            onDataChanged={onDataChanged}
            onRow={(record: AidPreferenceEntity) => {
                return {
                    onClick: () => onRowClick(record),
                };
            }}
            resourceUrl={resourceUrl}
            actions={[Actions.DELETE, Actions.ADD, Actions.COPY]}
            actionCallbacks={{ add: addCallback, delete: (item?: AidPreferenceEntity) => item && deleteCallback(item) }}
            addButtonName="aidPreferencesTable_addButton"
        />
    );
};
